import React, { useState, useEffect, useCallback } from 'react';
import { UncontrolledButtonDropdown, DropdownToggle } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort } from '@fortawesome/free-solid-svg-icons';
import { sortOrderForm, sortOrderList } from '../../redux/actions';
import { useSelector } from 'react-redux';
import SortOrderDialog from './components/SortOrderDialog';

/**
 * @typedef {Object} Column
 * @property {string} key - Unique key of the column (used for sorting)
 * @property {string} [text] - Display text for the column
 * @property {Function} [formatter] - Formatter function (if present column is not sortable)
 * @property {string} [type] - Column data type e.g. 'text', 'number'
 */

/**
 * @typedef {Object} SortSpec
 * @property {string} column - Column key being sorted
 * @property {1|-1} direction - 1 for ASC, -1 for DESC
 */

/**
 * Normalize different direction representations into 'ASC' | 'DESC'.
 *
 * Accepts numeric (1, -1) or string values ('asc', 'desc') and returns
 * the canonical uppercase string used in the UI.
 *
 * @param {1|-1|string|undefined} dir - Direction value to normalize
 * @returns {'ASC'|'DESC'|undefined} Normalized direction or undefined if unknown
 */
const normalizeDirection = dir => {
  if (dir === 1) return 'ASC';
  if (dir === -1) return 'DESC';
  if (typeof dir === 'string') return dir.toUpperCase();
};

/**
 * SortOrderButton component renders a dropdown that opens the SortOrderDialog.
 *
 * Props:
 * @param {{columns?: Column[], currentSort?: Partial<SortSpec>, onSortChange?: (sort: SortSpec) => void, listId: string, dispatch: Function}} props
 * @returns {JSX.Element}
 */
const SortOrderButton = React.memo(
  ({ columns = [], currentSort = {}, onSortChange, listId, dispatch }) => {
    const { data: initialValues = [] } = useSelector(
      state => state.sortOrderListData.data
    );

    const currentSortOrder =
      initialValues.find(so => so.GridId === listId) || {};

    const sortableColumns = (columns || []).filter(col => {
      if (col.formatter) return false;
      if (col.type && !['text', 'number'].includes(col.type)) return false;
      return true;
    });

    const [newSort, setNewSort] = useState({
      column:
        currentSortOrder.Column ||
        currentSort.column ||
        sortableColumns[0]?.key ||
        '',
      direction: normalizeDirection(
        currentSortOrder.Direction || currentSort.direction
      )
    });

    /** --- Sync when redux list updates ---
     *
     * If a saved sort for this grid exists in redux, update internal state
     * and notify parent via onSortChange with numeric direction.
     */
    useEffect(() => {
      if (currentSortOrder.Column) {
        const updatedSort = {
          column: currentSortOrder.Column,
          direction: normalizeDirection(currentSortOrder.Direction)
        };
        setNewSort(updatedSort);

        const converted = {
          column: updatedSort.column,
          direction: updatedSort.direction === 'ASC' ? 1 : -1
        };
        onSortChange && onSortChange(converted);
      }
    }, [JSON.stringify(currentSortOrder)]);

    /** --- Sync when props.currentSort updates ---
     *
     * When parent passes a currentSort prop update, reflect it in local state.
     */
    useEffect(() => {
      if (
        currentSort &&
        (currentSort.column || currentSort.direction !== undefined)
      ) {
        setNewSort({
          column: currentSort.column || sortableColumns[0]?.key || '',
          direction: normalizeDirection(currentSort.direction)
        });
      }
    }, [JSON.stringify(currentSort), JSON.stringify(columns)]);

    /** --- Load list on mount ---
     *
     * Request saved sort orders for this grid on mount.
     */
    useEffect(() => {
      dispatch(
        sortOrderList({
          filters: { GridId: listId },
          action: 'list',
          limit: 25,
          skip: 0,
          sort: { sort: -1 }
        })
      );
    }, [listId, dispatch]);

    /** --- Handle Apply ---
     *
     * Persist the selected sort and notify parent with numeric direction.
     */
    const handleApply = useCallback(() => {
      if (!newSort.column) return;
      const converted = {
        column: newSort.column,
        direction: newSort.direction === 'ASC' ? 1 : -1
      };

      onSortChange && onSortChange(converted);

      dispatch(
        sortOrderForm({
          action: currentSortOrder._id ? 'update' : 'add',
          ...currentSortOrder,
          GridId: listId,
          Column: newSort.column,
          Direction: newSort.direction,
          tableName: 'CATSoftwareVersion'
        })
      );

      dispatch(
        sortOrderList({
          filters: { GridId: listId },
          action: 'list',
          limit: 25,
          skip: 0,
          sort: { sort: -1 }
        })
      );
    }, [newSort, currentSortOrder, dispatch, listId, onSortChange]);

    return (
      <UncontrolledButtonDropdown>
        <DropdownToggle
          color="info"
          caret
          className="d-flex align-items-center"
          style={{
            gap: '0.25rem',
            padding: '0.35rem 0.75rem'
          }}
        >
          <FontAwesomeIcon icon={faSort} />
          <span>Sort Order</span>
        </DropdownToggle>

        <SortOrderDialog
          sortableColumns={sortableColumns}
          newSort={newSort}
          setNewSort={setNewSort}
          handleApply={handleApply}
        />
      </UncontrolledButtonDropdown>
    );
  }
);

export default SortOrderButton;
